import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@bs/contracts';
import { api, rememberCsrf, UNAUTH_EVENT } from '../api/instance.js';
import { ApiError } from '../api/client.js';

interface SessionCtx {
  me: Me | null;
  loading: boolean;
  vertical: string; // 'all' or vertical key
  setVertical: (v: string) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);
const VKEY = 'bs.vertical';

function readVertical(): string {
  try { return localStorage.getItem(VKEY) ?? 'all'; } catch (e) { console.warn('localStorage unavailable', e); return 'all'; }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [vertical, setV] = useState<string>(readVertical);
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.call('me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const onUnauth = () => { rememberCsrf(null); qc.setQueryData(['me'], null); };
    window.addEventListener(UNAUTH_EVENT, onUnauth);
    return () => window.removeEventListener(UNAUTH_EVENT, onUnauth);
  }, [qc]);

  const setVertical = useCallback((v: string) => {
    setV(v);
    if (v !== 'all') { try { localStorage.setItem('bs.lastArea', v); } catch (e) { console.warn('localStorage unavailable', e); } }
    try { localStorage.setItem(VKEY, v); } catch (e) { console.warn('localStorage unavailable', e); }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.call('logout');
    } finally {
      rememberCsrf(null);
      qc.clear();
      qc.setQueryData(['me'], null);
    }
  }, [qc]);

  return (
    <Ctx.Provider value={{ me: meQ.data ?? null, loading: meQ.isPending, vertical, setVertical, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('SessionProvider missing');
  return c;
}

/** `vertical` query param for list endpoints; undefined means all of the user's verticals. */
export const verticalParam = (v: string) => (v === 'all' ? undefined : v);
